import { Box, Grid2, Paper, Typography, useTheme } from "@mui/material";
import { useIsMobile } from "../../hook/useIsMobile";
import UpdateFieldNavigation from "../../components/updateFieldNavigation/updateFieldNavigation.component";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { fetchCropsStart } from "../../store/features/crops/cropsSlice";
import { fetchIrrigationTypesStart } from "../../store/features/irrigationTypes/irrigationTypesSlice";
import { fetchFieldsTypeStart } from "../../store/features/fieldsType/fieldsTypeSlice";
import FormActionButton from "../../components/buttons/formActionButton.component";
import {
  deleteFieldStart,
  fetchFieldDetailStart,
  resetUpdateFieldState,
  updateFieldStart,
} from "../../store/features/customer/field/fieldSlice";
import FieldMapForm from "../../components/forms/fieldMapForm/fieldMapForm.component";
import SubAreaForm from "../../components/forms/subAreaForm/subAreaForm.component";
import CustomAlert from "../../components/alert/alert.component";
import DeleteFieldForm from "../../components/forms/deleteField/deleteFieldForm.component";
import GenericInfoForm from "../../components/forms/genericInfoForm/genericInfoForm.component";
import CropHistoryForm from "../../components/forms/cropHistory/cropHistoryForm.component";
import {
  formatCropHistory,
  formatFieldMap,
  formatGenericInfo,
  formatSubAreas,
} from "../../utils/fieldFormatUtils";
import { fetchFieldsStart } from "../../store/features/customer/fields/fieldsSlice";
import { getFieldPmtilesUrl } from "../../utils/fieldMediaUtils";
import { getErrorMessage } from "../../utils/errorUtils";

const getSectionKey = (navItem) => {
  const sectionMap = {
    "Info generali": "genericInfo",
    Mappa: "fieldMap",
    "Storico colture": "cropHistory",
    Sottoterreni: "subAreas",
    "Elimina terreno": "deleteField",
  };
  return sectionMap[navItem];
};

const fieldSectionByValue = {
  genericInfo: "Info generali",
  cropHistory: "Storico colture",
  fieldMap: "Mappa",
  subAreas: "Sottoterreni",
  deleteField: "Elimina terreno",
};

const fieldSectionValueByLabel = Object.fromEntries(
  Object.entries(fieldSectionByValue).map(([value, label]) => [label, value])
);

const getSelectedNavItemFromParams = (searchParams) => {
  const section = searchParams.get("section");
  return fieldSectionByValue[section] || "Info generali";
};

const getFormId = (selectedNavItem) => {
  const formIdMap = {
    "Info generali": "generic-info-form",
    "Storico colture": "crop-history-row-form",
    Mappa: "map-form",
    Sottoterreni: "sub-areas-form",
  };

  return formIdMap[selectedNavItem];
};

const extractApiList = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.results)) {
    return payload.results;
  }

  return [];
};

const extractApiObject = (payload) => {
  if (payload?.data && !Array.isArray(payload.data)) {
    return payload.data;
  }

  return payload;
};

const isSameId = (left, right) => String(left) === String(right);

const UpdateFieldPage = () => {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fieldDetailResponse = useSelector((state) => state.field?.data);
  const accessToken = useSelector((state) => state.auth?.user?.access_token);
  const cropsResponse = useSelector((state) => state.crops?.data);
  const irrigationTypesResponse = useSelector(
    (state) => state.irrigationTypes?.data
  );
  const fieldsTypeResponse = useSelector((state) => state.fieldsType?.data);
  const { success, isLoading, error } = useSelector((state) => state.field);

  const fieldDetail = extractApiObject(fieldDetailResponse);
  const crops = extractApiList(cropsResponse);
  const irrigationTypes = extractApiList(irrigationTypesResponse);
  const fieldsType = extractApiList(fieldsTypeResponse);
  const hasFieldDetailForCurrentRoute =
    fieldDetail?.id && isSameId(fieldDetail.id, id);
  const fieldToUpdate = hasFieldDetailForCurrentRoute ? fieldDetail : null;
  const pmtilesUrl = getFieldPmtilesUrl(fieldToUpdate);

  const [formData, setFormData] = useState(fieldToUpdate || {});
  const [selectedNavItem, setSelectedNavItem] = useState(() =>
    getSelectedNavItemFromParams(searchParams)
  );
  const [hasSubAreas, setHasSubAreas] = useState(
    fieldToUpdate?.has_sub_field ?? true
  );
  const [hasPolygonChanged, setHasPolygonChanged] = useState(false);

  const handleSectionChange = (section, updatedData) => {
    setFormData((prevData) => ({
      ...prevData,
      [section]: updatedData,
    }));
  };

  const handleNavItemSelect = useCallback(
    (navItem) => {
      const sectionValue = fieldSectionValueByLabel[navItem];
      const nextSearchParams = new URLSearchParams(searchParams);

      if (sectionValue && sectionValue !== "genericInfo") {
        nextSearchParams.set("section", sectionValue);
      } else {
        nextSearchParams.delete("section");
      }

      setSelectedNavItem(navItem);
      setSearchParams(nextSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleChangeSave = (data) => {
    if (!fieldToUpdate) {
      return;
    }

    const sectionKey = getSectionKey(selectedNavItem);
    let formattedData = [];

    if (sectionKey && data) {
      const updatePayload = {
        [sectionKey]: data,
      };

      if (sectionKey === "genericInfo") {
        formattedData = formatGenericInfo(updatePayload, fieldToUpdate);
      } else if (sectionKey === "cropHistory") {
        formattedData = formatCropHistory(updatePayload, fieldToUpdate);
      } else if (sectionKey === "fieldMap") {
        formattedData = formatFieldMap(
          updatePayload,
          fieldToUpdate,
          hasPolygonChanged
        );
      } else if (sectionKey === "subAreas") {
        formattedData = formatSubAreas(updatePayload, fieldToUpdate);
      }

      dispatch(
        updateFieldStart({
          data: formattedData,
          fieldId: id,
          accessToken: accessToken,
        })
      );
    }
  };

  const handleDeleteField = () => {
    dispatch(deleteFieldStart({ fieldId: id, accessToken: accessToken }));
  };

  const handleHasSubAreasChange = (newHasSubAreas) => {
    setHasSubAreas(newHasSubAreas);

    if (!newHasSubAreas && selectedNavItem === "Sottoterreni") {
      handleNavItemSelect("Info generali");
    }
  };

  useEffect(() => {
    const nextSelectedNavItem = getSelectedNavItemFromParams(searchParams);
    setSelectedNavItem(nextSelectedNavItem);
  }, [searchParams]);

  useEffect(() => {
    if (fieldToUpdate) {
      setFormData(fieldToUpdate);
      setHasSubAreas(fieldToUpdate?.has_sub_field ?? true);

      if (!fieldToUpdate?.has_sub_field && selectedNavItem === "Sottoterreni") {
        handleNavItemSelect("Info generali");
      }
    }
  }, [fieldToUpdate, handleNavItemSelect, selectedNavItem]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    dispatch(fetchCropsStart({ accessToken: accessToken }));
    dispatch(fetchIrrigationTypesStart({ accessToken: accessToken }));
    dispatch(fetchFieldsTypeStart({ accessToken: accessToken }));
  }, [accessToken, dispatch]);

  useEffect(() => {
    if (id && accessToken) {
      dispatch(fetchFieldDetailStart({ accessToken, fieldId: id }));
    }
  }, [accessToken, dispatch, id]);

  const handleAlertClose = () => {
    dispatch(resetUpdateFieldState());
  };

  useEffect(() => {
    if (success && accessToken) {
      dispatch(fetchFieldsStart({ accessToken }));

      if (selectedNavItem === "Elimina terreno") {
        dispatch(resetUpdateFieldState());
        navigate("/userLands");
      }
    }
  }, [success, selectedNavItem, dispatch, navigate, accessToken]);

  return (
    <Box
      width="100%"
      height="100%"
      pt={isMobile ? 0 : 2}
      overflow={isMobile ? "auto" : ""}
    >
      <Paper
        sx={{
          p: 2,
          width: "100%",
          height: isMobile ? "auto" : "100%",
        }}
        elevation={0}
      >
        <Grid2 container spacing={2} height="100%">
          <Grid2 size={{ xs: 12, md: 2 }}>
            <Box
              width="100%"
              height="100%"
              bgcolor={theme.palette.custom.backgroundColor}
              p={2}
            >
              <UpdateFieldNavigation
                theme={theme}
                selectedItem={selectedNavItem}
                onItemSelect={handleNavItemSelect}
                fieldToUpdate={fieldToUpdate}
                hasSubAreas={hasSubAreas}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} height="100%">
            <Box
              width="100%"
              height="100%"
              display="flex"
              flexDirection="column"
            >
              <Box width="100%" height="100%" overflow="auto">
                {selectedNavItem === "Info generali" && (
                  <>
                    <GenericInfoForm
                      fieldsType={fieldsType}
                      irrigationTypes={irrigationTypes}
                      crops={crops}
                      theme={theme}
                      initialData={fieldToUpdate}
                      isEditMode={true}
                      onSubmit={(data) => handleChangeSave(data)}
                      onHasSubAreasChange={handleHasSubAreasChange}
                    />
                  </>
                )}
                {selectedNavItem === "Storico colture" && (
                  <>
                    <CropHistoryForm
                      theme={theme}
                      crops={crops}
                      onSubmit={(data) => handleChangeSave(data)}
                      formData={fieldToUpdate?.field_colture || []}
                      fieldToUpdate={fieldToUpdate}
                    />
                  </>
                )}
                {selectedNavItem === "Mappa" && (
                  <>
                    <Typography fontWeight="bold" fontSize="16px">
                      Mappa
                    </Typography>
                    <FieldMapForm
                      key={id}
                      formData={formData.polygon}
                      fieldPosition={fieldToUpdate?.position}
                      pmtilesUrl={pmtilesUrl}
                      onDataChange={(data) =>
                        handleSectionChange("fieldMap", data)
                      }
                      onSubmit={(data) => handleChangeSave(data)}
                      isEditMode
                      onPolygonChange={(hasChanged) =>
                        setHasPolygonChanged(hasChanged)
                      }
                    />
                  </>
                )}
                {selectedNavItem === "Sottoterreni" && (
                  <SubAreaForm
                    fieldsType={fieldsType}
                    formData={formData.field_part || fieldToUpdate?.field_parts}
                    onDataChange={(data) =>
                      handleSectionChange("field_part", data)
                    }
                    fieldPosition={fieldToUpdate?.polygon}
                    onSubmit={(data) => handleChangeSave(data)}
                  />
                )}
                {selectedNavItem === "Elimina terreno" && (
                  <DeleteFieldForm
                    handleDeleteField={handleDeleteField}
                    success={success}
                  />
                )}
              </Box>
              {selectedNavItem !== "Elimina terreno" && (
                <Box
                  width="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="end"
                  pt={1}
                >
                  <FormActionButton
                    label="Salva modifiche"
                    disabled={isLoading || !fieldToUpdate}
                    type="submit"
                    form={getFormId(selectedNavItem)}
                  />
                </Box>
              )}
            </Box>
          </Grid2>
        </Grid2>
        {success && (
          <CustomAlert
            severity="success"
            message="Modifiche salvate con successo"
            onClose={handleAlertClose}
          />
        )}
        {error && (
          <CustomAlert
            severity="error"
            message={getErrorMessage(error, {
              fallback:
                "Non è stato possibile salvare i dati. Riprova più tardi.",
            })}
            onClose={handleAlertClose}
          />
        )}
      </Paper>
    </Box>
  );
};

export default UpdateFieldPage;
